import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { CourtDecisionType, type SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseCourtDecisionForm, { residualErrors } from '../../../../hooks/useLegalCaseCourtDecisionForm';
import type { AddCourtDecisionValue } from '../../../../hooks/useLegalCaseCourtDecisions';

interface LegalCaseCourtDecisionFormProps {
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    // Quando presente, o form é de um recurso da decisão de origem: tipo travado, rótulos e
    // vínculo ajustados.
    relatedDecisionId?: string;
    onSubmit: (value: AddCourtDecisionValue) => Promise<boolean>;
    onCancel: () => void;
}

// "Recurso interposto" (APPEAL_FILED) só existe pelo botão "Recorrer" (que fornece a decisão de
// origem). No form comum ("Registrar decisão") não há origem, então escolher esse tipo geraria
// um recurso órfão (recusado com 422 no backend). Fora do modo recurso, some das opções; no
// modo recurso o select fica travado nesse valor, então mantém. Extraída para fora do
// componente (diretriz do repo: sem função dentro de função).
const visibleTypeOptions = (typeOptions: SelectOption[], isAppeal: boolean): SelectOption[] =>
    isAppeal
        ? typeOptions
        : typeOptions.filter((o) => o.value !== CourtDecisionType.APPEAL_FILED);

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseCourtDecisionForm.
// Serve à decisão comum e ao recurso (quando recebe relatedDecisionId): no recurso o tipo
// nasce travado em "Recurso interposto".
const LegalCaseCourtDecisionForm = ({
    typeOptions,
    errorFields,
    isSaving,
    relatedDecisionId,
    onSubmit,
    onCancel,
}: LegalCaseCourtDecisionFormProps) => {
    const form = useLegalCaseCourtDecisionForm({ onSubmit, relatedDecisionId });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            {form.isAppeal && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Recurso interposto a partir da decisão selecionada
                </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Tipo da decisão"
                    items={visibleTypeOptions(typeOptions, form.isAppeal)}
                    value={form.decisionType}
                    onChange={(e) => form.setDecisionType(e.target.value as CourtDecisionType)}
                    errorMessage={getErrorMessage(errorFields, 'decisionType')}
                    disabled={form.isAppeal}
                />
                <DateField
                    label={form.isAppeal ? 'Data do recurso' : 'Data da decisão'}
                    value={form.decisionDate}
                    onChange={form.setDecisionDate}
                    errorMessage={getErrorMessage(errorFields, 'decisionDate')}
                />
            </div>

            <TextEdit
                label={form.isAppeal ? 'Observações do recurso (opcional)' : 'Teor (opcional)'}
                value={form.summary}
                onChange={(e) => form.setSummary(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'summary')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : form.isAppeal ? 'Registrar recurso' : 'Registrar decisão'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseCourtDecisionForm;
